import { AwaitContainer } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpertReportEvaluation } from '@/entities/expert-report';
import type { EvaluationBlock, EvaluationRuleResult } from '@/entities/expert-report';
import { ExpertReportEvaluationTable } from './ExpertReportEvaluationTable';

type Props = {
  conversationId: number;
  expertReportId: number;
};

export type FlatRow = {
  id: string;
  blockId: number;
  name: string;
  /** Балл, рассчитанный системой */
  rating: number;
  maxRating: number;
  /** Балл, выставленный пользователем; null — балл не выставлялся */
  manualRating: number | null;
  rules?: EvaluationRuleResult[];
  depth: number;
  isParent: boolean;
  isTotal: boolean;
};

const flattenBlocks = (blocks: EvaluationBlock[], depth: number): FlatRow[] => {
  const rows: FlatRow[] = [];
  for (const block of blocks) {
    const isLeaf = block.children.length === 0;
    rows.push({
      id: `block-${block.blockId}`,
      blockId: block.blockId,
      name: block.blockName,
      rating: block.rating,
      maxRating: block.maxRating,
      manualRating: block.manualRating ?? null,
      rules: isLeaf ? block.rules : undefined,
      depth,
      isParent: !isLeaf,
      isTotal: false,
    });
    if (!isLeaf) rows.push(...flattenBlocks(block.children, depth + 1));
  }
  return rows;
};

export const ExpertReportTab: FC<Props> = ({ conversationId, expertReportId }) => {
  const { t } = useTranslation(['dialogs']);
  const { data, isLoading } = useExpertReportEvaluation(conversationId, expertReportId);
  const rows = useMemo<FlatRow[]>(() => {
    if (!data?.evaluation) return [];
    const { evaluation } = data;
    const totalRow: FlatRow = {
      id: 'total',
      blockId: 0,
      name: t('expertReport.total'),
      rating: evaluation.totalRating,
      maxRating: evaluation.totalMaxRating,
      manualRating: null,
      depth: 0,
      isParent: false,
      isTotal: true,
    };
    return [totalRow, ...flattenBlocks(evaluation.blocks, 0)];
  }, [data, t]);

  if (isLoading) return <AwaitContainer isLoading />;
  return <ExpertReportEvaluationTable rows={rows} expertReportId={expertReportId} conversationId={conversationId} />;
};
